import { Box } from "@mui/system";
import AirlineIcon from "@/components/custom/FlightPicker/FlightsSelect/AirlineIcon";
import {
  FlightSearchResult,
  HeadingLevel,
  SelectedTransport,
  TransportType,
} from "@/core/types/types";
import { IFormatedFlightData } from "@/components/custom/FlightPicker/FlightsSelect/FlightsSelect";
import { SvgIconTypeMap, Typography } from "@mui/material";
import React, { ComponentType } from "react";
import { DefaultComponentProps } from "@mui/material/OverridableComponent";
import DynamicMaterialIcons from "@/core/components/MaterialIcons/MaterialIcons";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";

interface CustomizedFlightProps {
  flight: FlightSearchResult | SelectedTransport;
  formattedFlightData: IFormatedFlightData;
}

const TRANSPORT_ICONS: {
  [key in TransportType as string]: ComponentType<
    DefaultComponentProps<SvgIconTypeMap>
  >;
} = {
  [TransportType.train]: DynamicMaterialIcons["DirectionsRailway"],
  [TransportType.bus]: DynamicMaterialIcons["DirectionsBus"],
  [TransportType.plane]: DynamicMaterialIcons["AirplanemodeActive"],
};

const styles = {
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px",
    border: "1px solid black",
    borderRadius: "50%",
    position: "relative",
  },
  dateLabel: {
    lineHeight: "1.2",
    display: "flex",
    alignItems: "center",
    gap: 3,
  },
};

const TransportIcon = ({ transportType }: { transportType: TransportType }) => {
  const classes: any = useCustomStyles(styles);
  const MuiTransportIcon = TRANSPORT_ICONS[transportType];

  return (
    <Box sx={classes?.iconContainer}>
      <MuiTransportIcon />
    </Box>
  );
};

export const TransportSelectedLabel = ({
  flight,
  formattedFlightData,
}: CustomizedFlightProps) => {
  const {
    formattedDepartureDate,
    formattedDepartureTime,
    formattedArrivalTime,
    formattedFlightNumber,
  } = formattedFlightData;
  const {
    transportTypeName,
    operatingAirlineName,
    operatingAirlineCode,
    airlineIata,
  } = flight as FlightSearchResult & SelectedTransport;
  const classes: any = useCustomStyles(styles);
  const transportTranslations: { [key in TransportType as string]: string } = {
    [TransportType.plane]: t("transportationMode.flight"),
    [TransportType.bus]: t("transportationMode.bus"),
    [TransportType.train]: t("transportationMode.train"),
  };
  const airlineCode = operatingAirlineCode ?? airlineIata ?? null;
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {airlineCode ? (
        <AirlineIcon
          flightNumber={formattedFlightNumber}
          src={`images/icons/airlines/${airlineCode?.toLowerCase()}.svg`}
        />
      ) : (
        <TransportIcon transportType={transportTypeName} />
      )}
      <Box ml={1} sx={{ width: "calc(100% - 65px)" }}>
        <Box>
          <Typography variant={HeadingLevel.Subtitle1} sx={classes?.dateLabel}>
            {formattedDepartureDate}{" "}
            <Typography variant={HeadingLevel.Body2}>|</Typography>{" "}
            {formattedDepartureTime} - {formattedArrivalTime}
          </Typography>
          <Box display="flex">
            <Box
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              <Typography variant={HeadingLevel.Body2}>
                {operatingAirlineName ??
                  transportTranslations[transportTypeName]}
              </Typography>
            </Box>
            {formattedFlightNumber && (
              <>
                <Box mx={0.5}>|</Box>
                <Box sx={{ whiteSpace: "nowrap" }}>{formattedFlightNumber}</Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
